import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Star } from "lucide-react";
import { fetchProducts, money } from "@/lib/api";
import Reveal from "@/components/Reveal";

const GUIDES = [
  {
    slug: "best-earbuds-under-150",
    title: "Best wireless earbuds under $150 in 2026",
    tag: "Audio",
    read: "4 min read",
    summary: "Great sound no longer costs $250. These are the two pairs we'd actually buy with our own money.",
    products: ["aero-buds-mini", "aero-buds-air"],
    body: [
      "The sub-$150 earbud market used to mean compromise: muddy bass, flaky connections, cases that died in a day. That era is over. After testing everything in the VOLTAIC vault, two pairs stand clearly above the rest — and neither asks you to spend more than a nice dinner out.",
      "The AERO Buds Mini are the everyday answer. At $129 you get a 10mm titanium driver with a warm, punchy tuning, four microphones with environmental noise cancellation that genuinely survives wind, and 8 hours per charge with another 24 in the case. At 4.1 grams per bud you forget they're in — which is the whole point of daily buds.",
      "If you want wireless charging and an open-fit design, the AERO Buds Air at $99 (down from $129) are the value pick. A bigger 13mm dynamic driver, 30 hours total with the case, and touch controls that actually register. They skip ANC, but for commutes, calls and podcasts, nothing under $100 touches them.",
      "Our verdict: commuters and gym-goers take the Mini. Open-fit fans and bargain hunters take the Air. Either way you're spending under $150 and getting hardware that would have been flagship two years ago.",
    ],
  },
  {
    slug: "mechanical-keyboards-guide",
    title: "Mechanical keyboards: which layout actually wins?",
    tag: "Accessories",
    read: "5 min read",
    summary: "75% TKL vs 65% compact — the honest breakdown for gamers, coders and hybrid desk setups.",
    products: ["voltstrike-tkl", "voltstrike-65"],
    body: [
      "Keyboard layout debates waste more time than the products save. Here's the short version: layout choice is about which keys you refuse to lose, not about which board looks best in photos.",
      "The 75% TKL layout — like our VOLTstrike TKL 75 — keeps the function row and navigation cluster. If you write code, use spreadsheets, or play games with F-key binds, this is your layout. The TKL 75 adds hall-effect magnetic switches with adjustable actuation (0.1mm to 4.0mm) and 8000Hz polling, which is the kind of latency advantage you can actually feel in competitive play.",
      "The 65% layout drops the function row for a board that fits in a backpack side-pocket. The VOLTstrike 65 Wireless is built exactly for that life: pre-lubed linear switches, three-device Bluetooth pairing, and a 4000mAh battery measured in weeks, not days. Writers, travelers and minimalist-desk people live here.",
      "The rule of thumb: if you hit F-keys or Delete/Home more than ten times a day, go 75%. If your desk moves or your setup is a laptop-plus-peripherals rig, go 65%. Both VOLTstrike boards are hot-swappable, so you're never locked into the switch feel you buy on day one.",
    ],
  },
  {
    slug: "smartwatch-buying-guide",
    title: "Smartwatch buying guide: battery life vs sensors",
    tag: "Wearables",
    read: "4 min read",
    summary: "The spec that matters most isn't on the box. How to pick between endurance and data.",
    products: ["chrono-apex", "pulse-horizon"],
    body: [
      "Every smartwatch comparison eventually collapses into one trade-off: how much battery you're willing to burn for how much data. A watch you charge daily tracks your sleep badly, because it's on the charger when you're asleep.",
      "The CHRONO Apex is the endurance flagship: grade-5 titanium, sapphire crystal, and 14 days of real smart-mode battery (35 in watch-only mode). Dual-frequency GPS lands you within a city block on trail runs, and ECG plus SpO2 are on-demand. At $799 it's for people who want a serious instrument, not a wrist notification screen.",
      "The PULSE Horizon flips the priority: a 1.4-inch always-on AMOLED, 120+ sport modes, recovery scoring, and temperature sensing — at $449 with 11-day battery. You give up the titanium and the dual-frequency GPS; you gain the richer daily training picture for nearly half the price.",
      "Buy the Apex if you train outdoors seriously or travel without chargers. Buy the Horizon if your training is gym-and-run based and you want every metric without the flagship price. Both sync to the same app, so switching later costs nothing.",
    ],
  },
];

export default function Guides() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const guide = GUIDES.find((g) => g.slug === slug);

  if (slug && !guide) {
    return (
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-32 text-center">
        <h1 className="font-display text-2xl font-bold">Guide not found</h1>
        <Link to="/guides" className="mt-4 inline-block text-voltaic-cyan">All guides</Link>
      </main>
    );
  }

  if (guide) {
    return (
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-32 lg:px-10" data-testid="guide-article">
        <Reveal>
          <Link to="/guides" data-testid="guide-back-link" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-voltaic-muted transition-colors hover:text-voltaic-cyan">
            <ArrowLeft size={13} /> All guides
          </Link>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">{guide.tag} · {guide.read}</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl">{guide.title}</h1>
          <p className="mt-4 text-base leading-relaxed text-voltaic-muted sm:text-lg">{guide.summary}</p>
          <div className="mt-8 space-y-5 text-base leading-relaxed text-voltaic-ink/90">
            {guide.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6" data-testid="guide-products">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-voltaic-cyan">Featured in this guide</p>
            <div className="mt-4 space-y-3">
              {guide.products.map((pid) => {
                const p = products.find((x) => x.id === pid);
                if (!p) return null;
                return (
                  <Link key={pid} to="/shop" data-testid={`guide-product-${pid}`} className="group flex items-center gap-4 rounded-2xl border border-voltaic-line p-3 transition-colors hover:border-voltaic-cyan/50">
                    <img src={p.image} alt={p.name} className="h-16 w-16 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{p.name}</p>
                      <p className="flex items-center gap-1.5 font-mono text-xs text-voltaic-muted">
                        <Star size={11} className="fill-voltaic-amber text-voltaic-amber" /> {p.rating.toFixed(1)} · {money(p.price)}
                      </p>
                    </div>
                    <ArrowRight size={16} className="shrink-0 text-voltaic-muted transition-transform group-hover:translate-x-1 group-hover:text-voltaic-cyan" />
                  </Link>
                );
              })}
            </div>
          </div>
        </Reveal>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 lg:px-10" data-testid="guides-page">
      <Reveal>
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">
          <BookOpen size={14} /> Buying guides
        </p>
        <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
          Read before you <span className="text-voltaic-cyan">charge</span>
        </h1>
        <p className="mt-3 max-w-lg text-voltaic-muted">Honest, spec-first guides written from the vault — no sponsored placements, no filler.</p>
      </Reveal>
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
        {GUIDES.map((g, i) => (
          <Reveal key={g.slug} delay={i * 0.06}>
            <Link to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="card-spotlight group flex h-full flex-col rounded-2xl border border-voltaic-line bg-voltaic-surface p-6 transition-colors hover:border-voltaic-cyan/50">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-cyan">{g.tag} · {g.read}</p>
              <h2 className="mt-3 font-display text-lg font-bold leading-snug">{g.title}</h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-voltaic-muted">{g.summary}</p>
              <span className="mt-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted transition-colors group-hover:text-voltaic-cyan">
                Read the guide <ArrowRight size={12} />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </main>
  );
}
